import React, { useCallback, useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';

function Select({label, placeholder, options, onSelect, formatItem, errorText, canOpen, value}) {
  const [selectedOption, setSelectedOption] = useState(value || '');
  const [isOpen, setIsOpen] = useState(false);
  const [isError, setError] = useState(false);
  const selectRef = useRef();

  // Update selectedOption when value prop changes (controlled component behavior)
  useEffect(() => {
    setSelectedOption(value || '');
  }, [value]);

  useEffect(() => {
    const checkIfClickedOutside = (e) => {
      if (isOpen && selectRef.current && !selectRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', checkIfClickedOutside);

    return () => {
      // Cleanup the event listener
      document.removeEventListener('mousedown', checkIfClickedOutside);
    };
  }, [isOpen]);

  const handleContainerClick = useCallback(() => {
    if(!canOpen) return setIsOpen(!isOpen);

    if(!canOpen()) {
        setError(true);
    } else {
        setError(false);
        setIsOpen(!isOpen);
    }
  },[isOpen, canOpen])

  const handleSelect = (selectedValue) => {
    // If component is controlled (value prop provided), don't update internal state
    // The parent will update the value prop which will trigger the useEffect above
    if (value === undefined) {
      // Uncontrolled mode - manage state internally
      setSelectedOption(selectedValue);
    }
    
    onSelect(selectedValue);
    setIsOpen(false);
    setError(false);
  };

  const renderList = (listData) => listData ? (
    <div className="
      absolute w-full bg-[#F8F9FB] text-[#231F20] top-2 rounded-md shadow-md mt-1 max-h-60 z-10 overflow-hidden
    ">
      <div className="h-[62px] border-b rounded-md" />
      <div className="max-h-60 overflow-auto pb-16">
        <ul>
          {listData.map((item, index) => (
            <li key={index} onClick={() => handleSelect(item)}
                className="px-6 py-3 pl-8 hover:bg-gray-100 cursor-pointer">
              {formatItem ? formatItem(item) : item}
            </li>
          ))}
        </ul>
      </div>
    </div>
  ) : null;

  // Use selectedOption for display (works for both controlled and uncontrolled modes)
  const displayValue = selectedOption;

  return (
    <div className="w-full px-4 py-2">
        <div
            ref={selectRef}
            onClick={() => handleContainerClick()}
            className="relative text-left rounded-md bg-[#F8F9FB] bg-opacity-80 h-[71px] cursor-pointer"
        >
            <div className={clsx('px-3 py-3 border-b rounded-md', isError && 'border border-red-800')} > 
                <label htmlFor="bagType" className={`
                    relative font-bold text-[#1C2938] pl-4 mb-2 ${isOpen ? 'z-20' : ''}
                `}>
                    {label}
                </label>
                <div className={`
                    relative w-full flex items-center justify-between pl-4 text-sm font-normal
                    text-[#1C2938] bg-transparent focus:outline-none rounded-md
                    ${isOpen ? 'z-20' : ''}
                `}>
                    <span>
                    { (options && displayValue) ? formatItem?.(displayValue) || displayValue
                        : <span className="opacity-50">{placeholder}</span>}
                    </span>
                    <svg
                    width="24" height="24" fill="rgba(35, 31, 32, 0.5)"
                    xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"
                    >
                    <path d="
                        M5.293 7.293a1 1 0 0 1 1.414 0L10 10.586l3.293-3.293a1
                        1 0 1 1 1.414 1.414l-4 4a1 1 0 0 1-1.414 0l-4-4a1 1 0 0 1 0-1.414z
                    "/>
                    </svg>
                </div>
            </div>
            {isError && (
                <div className="text-red-800 ms-4 mb-4 text-sm font-medium font-['Poppins']">{errorText}</div>
            )}
            {isOpen && options && renderList(options)}
            
        </div>
    </div>
  );
}

Select.propTypes = {
  label: PropTypes.string,
  placeholder: PropTypes.string,
  errorText: PropTypes.string,
  options: PropTypes.array,
  onSelect: PropTypes.func.isRequired,
  formatItem: PropTypes.func,
  canOpen: PropTypes.func,
  value: PropTypes.any // Add value prop to PropTypes
};

export default Select;
